/**
 * Where the dungeon being built is kept between visits.
 *
 * The editor's `modules/sandbox.ts` board is scratch space by design -- a place to try
 * two blocks against each other and then forget them. This one deliberately is not:
 * what the Build screen holds is a `DungeonLevel` document, and a document that a
 * reload empties is not worth twenty minutes of placing blocks. So every change is
 * written here and the screen restores it on load.
 *
 * `localStorage` is one browser, so this is a draft rather than a save: a dungeon that
 * other players can play belongs on the community server, which is the Community
 * Features objective rather than this one. Copy and Paste on the Build screen are what
 * carry a dungeon off this machine until then -- a twenty-block level is a couple of
 * kilobytes (§10 of develop/DungeonBlocks.md).
 *
 * **Every access is guarded and nothing here ever throws.** A browser with storage
 * denied -- private mode, a blocked third-party context -- must still build a dungeon,
 * so a failure to save or read is a `console.warn` and the screen carries on. Shaped
 * after the parked `admin-editor/src/editor/levelStore.ts`, with a key of its own
 * because that one holds a v1 tile grid this reader refuses by name.
 */
import {
  readLevel,
  type BlockCatalogue,
  type DungeonLevel,
  type LevelReadResult,
} from '@dungeon-builder/shared';

/** The v1 tile editor's draft lives under `dungeon-builder:draft-level`, not this. */
const STORAGE_KEY = 'dungeon-builder:build-level';

export function saveDraft(level: DungeonLevel): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(level));
  } catch (error) {
    console.warn('[build] could not save the draft', error);
  }
}

/**
 * The stored draft, read as a level.
 *
 * Three answers rather than two: `undefined` is no draft at all, which is a first
 * visit and nothing to report, while a `LevelReadResult` that is `ok: false` is a draft
 * this build will not open -- a v1 grid, or a block id the catalogue no longer holds --
 * and carries the reader's own sentence for the status line. Pass the catalogue so that
 * last case is caught here rather than drawn as holes.
 */
export function loadDraft(catalogue?: BlockCatalogue): LevelReadResult | undefined {
  let raw: string | null;
  try {
    raw = localStorage.getItem(STORAGE_KEY);
  } catch (error) {
    console.warn('[build] could not read the draft', error);
    return undefined;
  }
  if (raw === null || raw === '') return undefined;
  return readLevel(raw, catalogue);
}

export function clearDraft(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.warn('[build] could not clear the draft', error);
  }
}
